import { useState, type FormEvent } from "react";
import { api, ApiError, type AdminUserRow } from "../../api";
import { useAsync } from "../../useAsync";
import { StatusChip } from "../../components/StatusChip";
import { count, shortTime, DASH } from "../../format";
import { go } from "../../router";
import { getSession } from "../../session";

export function AdminUsers() {
  const [q, setQ] = useState("");
  const [staff, setStaff] = useState(false);
  const [applied, setApplied] = useState<{ q?: string; staff?: boolean }>({});
  const list = useAsync(() => api.admin.users({ ...applied, limit: 80 }), [applied]);
  const me = getSession()?.account.email;
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [link, setLink] = useState<string | null>(null);

  const search = (e: FormEvent) => {
    e.preventDefault();
    setApplied({ q: q.trim() || undefined, staff: staff || undefined });
  };

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    setRowError((r) => {
      const { [id]: _drop, ...rest } = r;
      return rest;
    });
    try {
      await fn();
      list.reload();
    } catch (err) {
      setRowError((r) => ({ ...r, [id]: err instanceof ApiError ? err.message : String(err) }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ marginTop: 18 }}>
      <form onSubmit={search} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        <div className="field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="u-q">Search</label>
          <input id="u-q" className="input" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Email or name" />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-2)" }}>
          <input type="checkbox" checked={staff} onChange={(e) => setStaff(e.target.checked)} />
          Staff / admins
        </label>
        <button type="submit" className="btn-accent" style={{ minHeight: 42 }}>Filter</button>
      </form>

      {link && (
        <div className="banner" style={{ marginBottom: 14 }}>
          <code style={{ fontSize: 11.5, wordBreak: "break-all" }}>{link}</code>
        </div>
      )}

      {list.error && <div className="form-error">{list.error}</div>}
      {list.loading && !list.data && <div style={{ color: "var(--text-faint)" }}>Loading…</div>}
      {list.data && (
        <>
          <div className="sub" style={{ marginBottom: 10 }}>{count(list.data.total)} people</div>
          <div style={{ overflowX: "auto" }}>
            <table className="ptable">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Admin</th>
                  <th>Workspaces</th>
                  <th>Last seen</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.data.users.map((u) => (
                  <UserRow key={u.id} u={u} isMe={u.email === me} busy={busy}
                           error={rowError[u.id]}
                           onAct={act} onLink={setLink} />
                ))}
                {list.data.users.length === 0 && (
                  <tr><td colSpan={5}><div className="cell" style={{ color: "var(--text-4)" }}>Nothing matches.</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function UserRow({
  u, isMe, busy, error, onAct, onLink,
}: {
  u: AdminUserRow; isMe: boolean; busy: boolean; error?: string;
  onAct: (id: string, fn: () => Promise<unknown>) => void;
  onLink: (url: string) => void;
}) {
  const sourceLabel = u.source === "granted" ? "granted" : u.source === "revoked" ? "revoked" : u.source === "domain" ? "domain" : "";
  return (
    <tr style={{ cursor: "default" }}>
      <td>
        <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          <span>
            {u.name || u.email}
            {isMe && <span className="eyebrow" style={{ marginLeft: 8 }}>you</span>}
            {u.disabled_at && <span className="eyebrow" style={{ marginLeft: 8, color: "var(--neg)" }}>disabled</span>}
            {!u.verified_at && <span className="eyebrow" style={{ marginLeft: 8 }}>unverified</span>}
            {u.google_linked && <span className="eyebrow" style={{ marginLeft: 8 }}>google</span>}
          </span>
          {u.name && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{u.email}</span>}
          {error && <span className="form-error" style={{ fontSize: 11.5 }}>{error}</span>}
        </div>
      </td>
      <td>
        <div className="cell" style={{ textAlign: "right" }}>
          {u.admin ? (
            <StatusChip kind="passing" label={sourceLabel ? `Admin · ${sourceLabel}` : "Admin"} />
          ) : u.source === "revoked" ? (
            <StatusChip kind="pre-gate" label="Revoked" />
          ) : (
            <span style={{ color: "var(--text-faint)" }}>{DASH}</span>
          )}
        </div>
      </td>
      <td>
        <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          {u.accounts.length === 0 && <span style={{ color: "var(--text-faint)" }}>{DASH}</span>}
          {u.accounts.map((a) => (
            <button key={a.id} type="button" className="btn-link" style={{ textAlign: "left" }}
                    onClick={() => go({ name: "admin", tab: "accounts", id: a.id })}>
              {a.name} · {a.member_role}
            </button>
          ))}
        </div>
      </td>
      <td><div className="cell num">{u.last_seen_at ? shortTime(u.last_seen_at) : DASH}</div></td>
      <td>
        <div className="cell" style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {!u.verified_at && (
            <button type="button" className="btn-link" disabled={busy}
                    onClick={() => onAct(u.id, async () => {
                      const res = await api.admin.verifyLink(u.id);
                      onLink(res.url);
                    })}>
              Verify link
            </button>
          )}
          <button type="button" className="btn-link" disabled={busy}
                  onClick={() => onAct(u.id, async () => {
                    const res = await api.admin.resetLink(u.id);
                    onLink(res.url);
                  })}>
            Reset link
          </button>
          {!isMe && (u.admin ? (
            <button type="button" className="btn-link" disabled={busy}
                    onClick={() => onAct(u.id, () => api.admin.setPlatformAdmin(u.id, false))}>
              Revoke admin
            </button>
          ) : (
            <button type="button" className="btn-link" disabled={busy}
                    onClick={() => onAct(u.id, () => api.admin.setPlatformAdmin(u.id, true))}>
              Grant admin
            </button>
          ))}
          {!isMe && (u.disabled_at ? (
            <button type="button" className="btn-link" disabled={busy}
                    onClick={() => onAct(u.id, () => api.admin.enableUser(u.id))}>
              Re-enable
            </button>
          ) : (
            <button type="button" className="btn-link" style={{ color: "var(--neg)" }} disabled={busy}
                    onClick={() => onAct(u.id, () => api.admin.disableUser(u.id))}>
              Disable
            </button>
          ))}
        </div>
      </td>
    </tr>
  );
}
